import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type TaskView } from '@apm/contracts';
import { CollapsibleSection } from '../../../components/ui/collapsible-section';
import {
  TaskEstimateDialog,
  TaskEstimateSummary,
} from '../../tasks/components/task-estimate-body';

/**
 * Khối "Ước lượng" của drawer — người dùng yêu cầu 2026-11-02. Ruột là
 * `TaskEstimateSummary`, cùng bảng với popover của ô SP trên board / Gantt / danh
 * sách: SP, thời gian, cách ước lượng, việc được so + SP của nó + chênh lệch.
 *
 * Gập sẵn như "Baseline, kế hoạch và thực tế"; số SP nằm ở dòng tiêu đề để gập
 * rồi vẫn trả lời được "việc này to cỡ nào".
 *
 * ⚠️ Không nhận asOf: đọc `TaskView` người gọi truyền vào, không tự lấy dữ liệu
 * theo thời gian; hộp ước lượng là thao tác ghi lên "bây giờ".
 */
export function TaskEstimateSection({
  task,
  canEdit = false,
}: {
  readonly task: TaskView;
  /** Sửa được việc này (`task:update` + `task.canEdit`) ⇒ có nút Ước lượng. */
  readonly canEdit?: boolean;
}): ReactNode {
  const { t } = useTranslation();
  const [estimating, setEstimating] = useState(false);
  const sp = task.storyPoints;
  const spText = sp === null ? t('actualTime.spNone') : t('actualTime.spValue', { value: sp });
  return (
    <CollapsibleSection
      title={t('actualTime.estimateTitle')}
      aside={<span data-testid="task-estimate-aside">{spText}</span>}
      testId="task-estimate"
      className="border-b border-border px-4 py-3"
    >
      <TaskEstimateSummary task={task} canEdit={canEdit} onEstimate={() => setEstimating(true)} />
      {estimating ? <TaskEstimateDialog task={task} onClose={() => setEstimating(false)} /> : null}
    </CollapsibleSection>
  );
}
